import {
  Dispatch,
  ForwardRefRenderFunction,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import { Pagination } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Container, ContainerProps, SimpleGrid, Stack } from '@mantine/core'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { formatExpertSummary } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { ExpertAuthorCard } from 'components/molecules/expertCard'
import { _NoResult } from 'components/pages/expert/_noResult'
import { _Skeleton } from 'components/pages/expert/_skeleton'
import { useIsMobile } from 'hooks/useIsMobile'
import { ExpertUserSummary } from 'interfaces/types'
import { useExpertStore } from 'state/expert'

type _ExpertListContainerProps = ContainerProps

export type _ExpertListHandle = {
  setCurrentPage: Dispatch<SetStateAction<number>>
}

const _ExpertListContainer: ForwardRefRenderFunction<
  _ExpertListHandle,
  _ExpertListContainerProps
> = (props, ref) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { expertList } = useExpertStore((state) => state, shallow)
  const [expertsPaging, setExpertsPaging] = useState<Array<ExpertUserSummary>>(
    [],
  )

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useImperativeHandle(ref, () => ({
    setCurrentPage,
  }))

  useEffect(() => {
    setExpertsPaging(expertList?.experts)
  }, [expertList?.experts])

  const handlePaging = async (page: number) => {
    if (page !== currentPage) {
      setIsLoading(true)
      setCurrentPage(page)
      const expertResponses = await callApiNew(
        PATHS.EXPERT.GET_ALL_EXPERTS_PAGING,
        {
          params: {
            page: page,
            perPage: 10,
          },
        },
      )
      const newExperts =
        expertResponses?.data?.experts?.map(formatExpertSummary) || null
      if (newExperts) {
        setExpertsPaging(newExperts)
        setIsLoading(false)
      }
    }
  }

  return (
    <Container size="lg" px={{ base: 0, md: 16 }} {...props}>
      <Stack spacing={4} mb={24}>
        <Text size="s1" weight="semiBold">
          {f({
            id: 'experts.listOfExperts',
            defaultMessage: 'List of Experts',
          })}
        </Text>
        <Text size="p3" color="gray.6">
          {f(
            {
              id: 'searchPage.numberOfResults',
              defaultMessage: '{no} kết quả được tìm thấy',
            },
            { no: expertList.pagination?.get_total || 0 },
          )}
        </Text>
      </Stack>
      {expertsPaging?.length > 0 ? (
        <>
          <SimpleGrid
            cols={2}
            spacing={24}
            verticalSpacing={16}
            breakpoints={[{ maxWidth: 755, cols: 1 }]}
          >
            {isLoading ? (
              <_Skeleton />
            ) : (
              expertsPaging.map((expert) => (
                <ExpertAuthorCard key={expert.id} expert={expert} />
              ))
            )}
          </SimpleGrid>
          {expertList.pagination && expertList.pagination?.max_num_pages > 1 && (
            <Container
              px={0}
              fluid={isMobile}
              my={24}
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Pagination
                pagesAmount={expertList.pagination?.max_num_pages || 0}
                currentPage={currentPage}
                onChange={handlePaging}
                style={{
                  justifyContent: 'flex-start',
                  gap: isMobile ? '2px' : '10px',
                }}
              />
            </Container>
          )}
        </>
      ) : (
        <_NoResult w={'100%'} ta="center" my={{ base: 56, md: 64 }} />
      )}
      {/* <_Policies /> */}
    </Container>
  )
}

export default forwardRef(_ExpertListContainer)
